/**
 * Map Polygon POIs (Points of Interest) Management
 * 
 * This module handles the creation, management, and styling of polygon POIs on Mapbox maps.
 * It supports filled polygons with borders, hover effects, and click interactions.
 * 
 * Features:
 * - Automatic color theming based on tour configuration
 * - Support for custom colors per polygon (hex, rgba, and 8-char hex with alpha)
 * - Automatic conversion of 8-character hex colors to rgba format for Mapbox compatibility
 * - Hover effects with color changes
 * - Click interactions for popups and scene navigation
 * - Accessibility support
 * 
 * Usage:
 * 
 * // Basic polygon POI with theme colors
 * const polygonPois = [{
 *   title: "Campus Quad",
 *   description: "Main campus quadrangle area",
 *   coordinates: [[lng1, lat1], [lng2, lat2], [lng3, lat3], ...], // nested array
 *   sceneIds: ["scene-id-1", "scene-id-2"]
 * }];
 * 
 * // Polygon POI with custom colors
 * const polygonPois = [{
 *   title: "Special Area",
 *   description: "Special access area",
 *   coordinates: [[lng1, lat1], [lng2, lat2], [lng3, lat3], ...],
 *   fillColor: "#ff00004D", // Red with 30% opacity (8-char hex converted to rgba)
 *   borderColor: "#ffffff",
 *   hoverFillColor: "#ff666699" // Lighter red with 60% opacity (8-char hex converted to rgba)
 * }];
 * 
 * await addPolygonPOIs({ map, polygonPois, bgColor, textColor, setTooltip, scenes, slug });
 */

import PopupMap from "./PopupMap.svelte";

function getBlackOrWhite(color) {
  return parseInt(color.replace("#", ""), 16) > 0xffffff / 2
    ? "#000000"
    : "#ffffff";
}

/**
 * Convert 8-character hex color (with alpha) to rgba format for Mapbox compatibility
 * @param {string} hexColor - Hex color in format #RRGGBBAA
 * @returns {string} RGBA color string
 */
function hexToRgba(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') return hexColor;
  
  // Check if it's an 8-character hex color with alpha
  const hex8Match = hexColor.match(/^#([0-9a-fA-F]{8})$/);
  if (hex8Match) {
    const hex = hex8Match[1];
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const a = parseInt(hex.substring(6, 8), 16) / 255; // Convert to 0-1 range
    
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  
  // Check if it's a 6-character hex color (no alpha)
  const hex6Match = hexColor.match(/^#([0-9a-fA-F]{6})$/);
  if (hex6Match) {
    const hex = hex6Match[1];
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `rgba(${r}, ${g}, ${b}, 1)`;
  }
  
  // Return as-is if not a hex color
  return hexColor;
}

/**
 * Convert nested coordinate array to GeoJSON Polygon format
 * @param {Array} coords - Nested array [[lng1, lat1], [lng2, lat2], ...]
 * @returns {Array} GeoJSON polygon coordinates [[[lng1, lat1], [lng2, lat2], ...]]
 */
function coordinatesToPolygon(coords) {
  // Ensure we have a valid coordinate array
  if (!Array.isArray(coords) || coords.length === 0) {
    return [[]];
  }
  
  // Validate that we have nested coordinate pairs
  if (!Array.isArray(coords[0]) || coords[0].length !== 2) {
    console.warn('Invalid coordinate format. Expected [[lng, lat], [lng, lat], ...]');
    return [[]];
  }
  
  const pairs = [...coords];
  // Close the ring if needed
  if (pairs.length > 0 && 
      (pairs[0][0] !== pairs[pairs.length - 1][0] || 
       pairs[0][1] !== pairs[pairs.length - 1][1])) {
    pairs.push([...pairs[0]]);
  }
  return [pairs]; // Polygon requires array of rings
}

/**
 * Generate popup data for polygon POI
 * @param {Object} poi - Polygon POI object
 * @param {Array} scenes - Available scenes
 * @returns {Object} Popup data object
 */
function getPopup(poi, scenes) {
  if (!poi) return;
  const { title, description } = poi;
  let { sceneIds, location } = poi;
  // Parse JSON strings
  if (typeof sceneIds === "string") sceneIds = JSON.parse(sceneIds);
  if (typeof location === "string") location = JSON.parse(location);

  // convert the scene ids into linked scenes
  const linkedscenes = sceneIds?.map((sceneId) => {
    const scene = scenes?.find((scene) => scene.id === sceneId);
    return {
      id: sceneId,
      name: scene?.name,
      title: scene?.title,
    };
  });

  return {
    title,
    content: description,
    linkedscenes,
    directions: location,
  };
}

/**
 * Remove polygon POIs from the map
 * @param {Object} map - Mapbox map instance
 */
export function removePolygonPOIs({ map }) {
  console.log("removePolygonPOIs");
  
  // Remove event listeners if they exist
  if (map._polygonPoiListeners) {
    Object.entries(map._polygonPoiListeners).forEach(([event, handler]) => {
      map.off(event, "polygon-pois-fill", handler);
      map.off(event, "polygon-pois-border", handler);
    });
    delete map._polygonPoiListeners;
  }

  // Remove all polygon POI layers
  const polygonLayers = ['polygon-pois-fill', 'polygon-pois-border'];
  polygonLayers.forEach(layer => {
    if (map.getLayer(layer)) {
      map.removeLayer(layer);
    }
  });

  // Remove the polygon POI source
  if (map.getSource('polygon-pois')) {
    map.removeSource('polygon-pois');
  }
}

/**
 * Add polygon POIs to the map
 * @param {Object} params - Parameters object
 * @param {Object} params.map - Mapbox map instance
 * @param {Array} params.polygonPois - Array of polygon POI objects
 * @param {string} params.bgColor - Background color for polygons
 * @param {string} params.textColor - Text color for tooltips
 * @param {Function} params.setTooltip - Tooltip setter function
 * @param {Array} params.scenes - Available scenes
 * @param {string} params.slug - Tour slug
 */
export async function addPolygonPOIs({ map, polygonPois, bgColor, textColor, setTooltip, scenes, slug }) {
  // Filter out hidden polygon POIs and sort by Map Menu order, then by name
  polygonPois = polygonPois.filter((poi) => !poi.hidden).sort((a, b) => {
    const oA = a.order ?? 0, oB = b.order ?? 0;
    if (oA !== oB) return oA - oB;
    return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
  });

  if (polygonPois.length === 0) return;
  
  // Setup colors with default alpha
  bgColor = bgColor || "#0000004D"; // Default 30% opacity
  textColor = textColor || getBlackOrWhite(bgColor);
  
  // Helper function to convert colors for Mapbox compatibility
  const convertColor = (color) => {
    if (!color || typeof color !== 'string') return color;
    
    // Convert hex colors ending with '00' to 'transparent'
    if (color.match(/^#[0-9a-fA-F]{8}$/) && color.endsWith('00')) {
      return 'transparent';
    }
    
    // Convert hex colors to rgba format for better Mapbox compatibility
    return hexToRgba(color);
  };
  
  // Convert polygon POIs to GeoJSON
  const geojsonSource = {
    type: "FeatureCollection",
    features: polygonPois.map((poi, index) => {
      const coordinates = coordinatesToPolygon(poi.coordinates);
      
      return {
        type: "Feature",
        id: poi.id || `polygon-poi-${index}`,
        properties: {
          ...poi,
          // Set default colors if not provided (with alpha channels)
          fillColor: convertColor(poi.fillColor || bgColor),
          borderColor: convertColor(poi.borderColor || textColor),
          borderWidth: poi.borderWidth || 2,
          hoverFillColor: convertColor(poi.hoverFillColor || poi.fillColor || bgColor),
          hoverBorderColor: convertColor(poi.hoverBorderColor || poi.borderColor || textColor),
        },
        geometry: {
          type: "Polygon",
          coordinates: coordinates,
        },
      };
    }),
  };

  // Add the polygon POI source
  map.addSource("polygon-pois", {
    type: "geojson",
    data: geojsonSource,
    generateId: true,
  });

  // Add fill layer
  map.addLayer({
    id: "polygon-pois-fill",
    type: "fill",
    source: "polygon-pois",
    paint: {
      "fill-color": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        ["get", "hoverFillColor"],
        ["get", "fillColor"]
      ],
    },
  });

  // Add border layer
  map.addLayer({
    id: "polygon-pois-border",
    type: "line",
    source: "polygon-pois",
    paint: {
      "line-color": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        ["get", "hoverBorderColor"],
        ["get", "borderColor"]
      ],
      "line-width": ["get", "borderWidth"],
    },
  });

  // Store event listeners for cleanup
  const listeners = {
    click: (e) => {
      console.log("polygon poi click", e.features[0].properties);
      const popup = getPopup(e.features?.[0]?.properties, scenes);
      if (popup) {
        const popupMap = new PopupMap({
          target: document.body,
          props: popup,
        });
      }
    },
    mousemove: (e) => {
      const x = e.point.x;
      const y = e.point.y;
      const text = e.features[0]?.properties?.title;
      if (text) setTooltip({ x, y, text });

      // Set hover state for color changes
      if (e.features.length > 0) {
        map.getCanvas().style.cursor = "pointer";
        
        // Reset previous hover state
        if (map._hoveredPolygonId !== null) {
          map.setFeatureState(
            { source: "polygon-pois", id: map._hoveredPolygonId },
            { hover: false }
          );
        }
        
        // Set new hover state
        map._hoveredPolygonId = e.features[0].id;
        if (map._hoveredPolygonId !== null) {
          map.setFeatureState(
            { source: "polygon-pois", id: map._hoveredPolygonId },
            { hover: true }
          );
        }
      }
    },
    mouseleave: () => {
      map.getCanvas().style.cursor = "";
      setTooltip(null);
      
      // Reset hover state
      if (map._hoveredPolygonId !== null) {
        map.setFeatureState(
          { source: "polygon-pois", id: map._hoveredPolygonId },
          { hover: false }
        );
        map._hoveredPolygonId = null;
      }
    }
  };

  // Add event listeners to both layers
  Object.entries(listeners).forEach(([event, handler]) => {
    map.on(event, "polygon-pois-fill", handler);
    map.on(event, "polygon-pois-border", handler);
  });

  // Store listeners on the map object for cleanup
  map._polygonPoiListeners = listeners;
}
